import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Notice, OnOffBadge, Switch } from '@/admin/AdminShell'
import { ApiError } from '@/lib/api'
import { fetchNextStepSwitch, saveNextStepSwitch } from './nextStepApi'

/**
 * Выключатель «повод от ИИ для следующего шага по сделке» (тикет #133).
 *
 * Засеян выключенным: включённый, он отправляет модели контекст каждой новой рекомендации, то есть
 * тратит токены. Выключенный, рекомендации всё равно находятся правилами и показываются — с
 * шаблонным поводом, подписанным как шаблон.
 */
export function NextStepSwitchPanel() {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const mayEdit = can(user, 'system.integration', 'update')
  const state = useQuery({ queryKey: ['admin-ai-next-step'], queryFn: ({ signal }) => fetchNextStepSwitch(signal) })
  const [notice, setNotice] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (enabled: boolean) => saveNextStepSwitch(enabled),
    onSuccess: (next) => {
      queryClient.setQueryData(['admin-ai-next-step'], next)
      setNotice(null)
    },
    onError: (failure: unknown) => setNotice(failure instanceof ApiError ? failure.message : t('app.error')),
  })
  if (state.isError) {
    return <Notice text={state.error instanceof ApiError ? state.error.message : t('app.error')} />
  }
  if (!state.data) return null
  return (
    <section className="panel stack" style={{ gap: 12 }} aria-label={t('nextStepAdmin.title')}>
      <div className="row" style={{ gap: 8 }}>
        <span className="h2">{t('nextStepAdmin.title')}</span>
        <OnOffBadge on={state.data.enabled} />
      </div>
      <p className="t-sm muted" style={{ margin: 0 }}>{t('nextStepAdmin.intro')}</p>
      <Switch
        checked={state.data.enabled}
        disabled={!mayEdit || save.isPending}
        label={t('nextStepAdmin.enabled')}
        onChange={(value) => save.mutate(value)}
      />
      <Notice text={notice} />
    </section>
  )
}
